<template>
  <SectionPageShell section="support" title="WordPress-Anleitungen" show-back-link>
    <p class="wordpress-guides__intro">
      Hier finden Sie Schritt-für-Schritt-Anleitungen zur Pflege Ihrer WordPress-Website.
      Klappen Sie ein Thema auf, um die Anleitung zu lesen.
    </p>

    <aside class="wordpress-guides__getting-started mb-4" aria-labelledby="wordpress-start-title">
      <h2 id="wordpress-start-title">Im WordPress-Verwaltungsbereich starten</h2>
      <p>
        Melden Sie sich unter <strong>ihre-website/wp-admin</strong> an. Links in der Navigation finden Sie die
        Inhalte, die Sie bearbeiten können: Medien zum Hochladen von Bildern und PDF-Dateien, Seiten,
        Zimmer, Unterkünfte oder Ferienwohnungen sowie gegebenenfalls Services.
      </p>
      <p class="mb-2"><strong>Barrierefreiheit mitbedenken:</strong> Beachten Sie bei der Bearbeitung die Barrierefreiheitsrichtlinien.</p>
      <ul class="mb-0">
        <li><a href="https://www.manet-marketing.de/2025/06/26/was-sie-bei-der-bearbeitung-ihrer-barrierenfreien-website-beachten-sollten/" target="_blank" rel="noopener noreferrer">Tipps zur Bearbeitung Ihrer barrierefreien Website (neuer Tab)</a></li>
        <li><a href="https://www.manet-marketing.de/2025/09/09/barrierefreie-pdfs-erstellen/" target="_blank" rel="noopener noreferrer">Barrierefreie PDFs erstellen (neuer Tab)</a></li>
      </ul>
    </aside>

    <section class="wordpress-guides" aria-labelledby="wordpress-guides-title">
      <div class="wordpress-guides__header">
        <h2 id="wordpress-guides-title">Ihre Website bearbeiten</h2>
        <p class="text-muted mb-0">Wählen Sie ein Thema. Die Bilder lassen sich zum genaueren Lesen vergrößern.</p>
      </div>

      <details v-for="guide in guides" :id="guide.id" :key="guide.id" class="wordpress-guides__item">
        <summary class="wordpress-guides__question">
          <span>{{ guide.question }}</span>
          <Icon icon="fa-pro-solid:chevron-down" class="wordpress-guides__chevron" aria-hidden="true" />
        </summary>
        <div class="wordpress-guides__answer">
          <p v-if="guide.description">{{ guide.description }}</p>
          <p v-if="guide.note" class="wordpress-guides__note"><strong>Empfehlung vorab:</strong> {{ guide.note }}</p>
          <ol v-if="guide.steps" class="wordpress-guides__steps">
            <li v-for="(step, index) in guide.steps" :id="step.id" :key="index" class="wordpress-guides__step">
              <h3>{{ step.title }}</h3>
              <p v-for="paragraph in step.text" :key="paragraph">{{ paragraph }}</p>
              <a v-if="step.guideLink" :href="`#${step.guideLink.id}`" @click.prevent="openGuide(step.guideLink.id)">{{ step.guideLink.label }}</a>
              <a v-if="step.asideLink" :href="`#${step.asideLink}`" class="wordpress-guides__aside-link" @click.prevent="focusAside(step.asideLink)">* Die Seitenleiste ist nicht sichtbar?</a>
              <p v-if="step.warning" class="wordpress-guides__warning"><strong>Wichtig:</strong> {{ step.warning }}</p>
              <GuideScreenshots :images="step.images" />
            </li>
          </ol>
          <h3 v-if="guide.examplesTitle" class="wordpress-guides__examples-title">{{ guide.examplesTitle }}</h3>
          <div v-for="example in guide.examples" :id="example.id" :key="example.title" :tabindex="example.id ? -1 : undefined" class="wordpress-guides__example">
            <h3>{{ example.title }}</h3>
            <p v-if="example.text">{{ example.text }}</p>
            <GuideScreenshots :images="example.images" />
          </div>
          <nav v-if="guide.related" class="wordpress-guides__related" aria-label="Passende Anleitungen">
            <strong>Passend dazu:</strong>
            <ul class="mb-0">
              <li v-for="related in guide.related" :key="related.id">
                <a :href="`#${related.id}`" @click.prevent="openGuide(related.id)">{{ related.label }}</a>
              </li>
            </ul>
          </nav>
          <router-link v-if="guide.route" :to="{ name: guide.route }" class="btn btn-outline-primary">{{ guide.linkLabel }}</router-link>
        </div>
      </details>
    </section>

    <div class="wordpress-guides__help mt-4">
      <p class="mb-2">Ihre Frage ist noch offen?</p>
      <router-link :to="{ name: 'account.support.create' }" class="btn btn-outline-primary">
        Ticket erstellen
      </router-link>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from '@/components/base_type/SectionPageShell.vue';
import GuideScreenshots from '@/components/support/GuideScreenshots.vue';
import { wordpressGuides } from '@/constants/wordpressGuides';

export default {
  name: 'SupportWordPressPage',
  components: {
    SectionPageShell,
    GuideScreenshots,
  },
  data() {
    return {
      guides: wordpressGuides,
    }
  },
  methods: {
    focusAside(id) {
      const aside = document.getElementById(id)
      if (!aside) return
      aside.scrollIntoView({ block: 'center' })
      aside.focus({ preventScroll: true })
    },
    openGuide(id) {
      const target = document.getElementById(id)
      const details = target?.closest('details')
      if (!details) return
      details.open = true
      target.scrollIntoView({ block: 'start' })
      details.querySelector('summary')?.focus({ preventScroll: true })
    },
  },
}
</script>

<style scoped lang="scss">
.wordpress-guides__intro {
  max-width: 75ch;
  margin-bottom: 1.5rem;
  color: #4f6273;
  line-height: 1.6;
}

.wordpress-guides {
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #dce4ea;
}

.wordpress-guides__header {
  padding: 1rem;
  border-bottom: 1px solid #e5edf3;

  h2 {
    margin-bottom: 0.5rem;
    color: #10273d;
    font-size: 1.15rem;
  }
}

.wordpress-guides__item + .wordpress-guides__item {
  border-top: 1px solid #e8eef4;
}

.wordpress-guides__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 3.25rem;
  padding: 0.9rem 1rem;
  color: #10273d;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid #10273d;
    outline-offset: -3px;
  }
}

.wordpress-guides__chevron {
  flex: 0 0 auto;
  color: #6b7f90;
}

.wordpress-guides__item[open] .wordpress-guides__chevron {
  transform: rotate(180deg);
}

.wordpress-guides__answer {
  padding: 0.5rem 1rem 1.5rem;
  color: #4f6273;
  line-height: 1.6;

  p {
    max-width: 75ch;
  }

  h3 {
    margin: 0 0 0.625rem;
    color: #10273d;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.5;
  }

  p:last-child {
    margin-bottom: 0;
  }
}

.wordpress-guides__getting-started {
  padding: 1rem;
  color: #4f6273;
  background: #fff;
  border: 1px solid #dce4ea;
  line-height: 1.6;

  h2 {
    color: #10273d;
    font-size: 1.15rem;
  }

  p {
    max-width: 80ch;
  }

  a {
    overflow-wrap: anywhere;
  }
}

.wordpress-guides__note,
.wordpress-guides__warning {
  padding: 1rem;
  background: #f3f6f8;
  border-left: 3px solid #dce4ea;
}

.wordpress-guides__warning {
  background: #fff8e6;
  border-left-color: #a96b00;
}

.wordpress-guides__steps {
  max-width: 85ch;
  margin: 1.5rem 0 0;
  padding-left: 1.75rem;
}

.wordpress-guides__step {
  padding-left: 0.5rem;

  &::marker {
    color: #10273d;
    font-weight: 700;
  }

  & + & {
    margin-top: 2rem;
  }
}

.wordpress-guides__example,
.wordpress-guides__related,
.wordpress-guides__examples-title {
  margin-top: 1.5rem;
}

.wordpress-guides__item,
.wordpress-guides__step {
  scroll-margin-top: 6rem;
}

.wordpress-guides__aside-link {
  display: inline-block;
  margin-bottom: 0.5rem;
}
</style>
